import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, Offcanvas, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faTimes,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";

const createGoogleMapsNavigationLink = (startLocation, endLocation) => {
  const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    startLocation
  )}&destination=${encodeURIComponent(endLocation)}`;
  window.open(url, "_blank", "noopener noreferrer");
};

const EmbeddedChatMap = ({ dest, source }) => {
  const [routeIndex, setRouteIndex] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState("");
  const [calcRoute, setCalcRoute] = useState(false);
  const [routeMode, setRouteMode] = useState("DRIVING");
  const [steps, setSteps] = useState([]);
  const mapRef = useRef(null);
  const directionsRenderer = useRef(null);
  const defaultMarker = useRef(null);
  const mapInstance = useRef(null);

  const [load, setLoad] = useState(true);

  // Initialize map and directions renderer
  useEffect(() => {
    if (mapRef.current && window.google) {
      const map = new window.google.maps.Map(mapRef.current, {
        // center: { lat: parseFloat(eventObject.lat), lng: parseFloat(eventObject.lon) },
        // zoom: 8,
      });

      mapInstance.current = map;

      directionsRenderer.current = new window.google.maps.DirectionsRenderer({
        map,
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: "#6f42c1",
          strokeWeight: 6,
        },
      });

      // Create geocoder to get destination coordinates
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: dest }, (results, status) => {
        if (status === "OK" && results[0]) {
          const position = results[0].geometry.location;
          // map.setCenter(position);
          // map.setZoom(12);

          defaultMarker.current = new window.google.maps.Marker({
            position,
            map,
            title: dest,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#6f42c1",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });
        }
      });
    }

    return () => {
      setLoad(true);
    };
  }, [mapRef.current]);

  // Calculate routes
  useEffect(() => {
    setTimeout(
      () => {
        setLoad(false);
        if (!source || !window.google || !mapRef.current) return;
        if (!routeMode) return;

        const directionsService = new window.google.maps.DirectionsService();
        setCalcRoute(true);
        const request = {
          origin: source,
          destination: dest,
          travelMode: window.google.maps.TravelMode[routeMode],
          provideRouteAlternatives: true,
          transitOptions:
            routeMode === "TRANSIT"
              ? {
                  departureTime: new Date(),
                  modes: ["BUS", "RAIL", "SUBWAY", "TRAIN", "TRAM"],
                }
              : undefined,
        };

        console.log(request);

        directionsService
          .route(request)
          .then((response) => {
            setCalcRoute(false);
            if (response.routes.length > 0) {
              setRoutes(response.routes);
              setSteps(response.routes[0].legs[0].steps);
              setError("");
              if (defaultMarker.current) defaultMarker.current.setMap(null);
              directionsRenderer.current.setDirections(response);
            }
          })
          .catch((err) => {
            console.error("Directions request failed:", err);
            setCalcRoute(false);
            setError(
              "No routes available for this transportation mode. Showing destination location."
            );
            setRoutes([]);
            setSteps([]);

            // Reset the directions renderer
            if (directionsRenderer.current) {
              directionsRenderer.current.setMap(null);
              directionsRenderer.current.setPanel(null);
            }

            directionsRenderer.current =
              new window.google.maps.DirectionsRenderer({
                map: mapInstance.current,
                suppressMarkers: false,
                polylineOptions: {
                  strokeColor: "#6f42c1",
                  strokeWeight: 6,
                },
              });

            // Show default marker
            if (defaultMarker.current) {
              defaultMarker.current.setMap(mapInstance.current);
              if (mapInstance.current) {
                // mapInstance.current.setCenter(defaultMarker.current.getPosition());
                // mapInstance.current.setZoom(12);
              }
            }
          });
      },
      load ? 3000 : 0
    );
  }, [source, dest, routeMode, mapRef.current]);

  const handleRouteClick = (index) => {
    setRouteIndex(index);
    setSteps(routes[index].legs[0].steps);
    directionsRenderer.current.setRouteIndex(index);
  };

  const transportModes = [
    { value: "DRIVING", label: "🚗 Driving" },
    { value: "TRANSIT", label: "🚌 Transit" },
    { value: "CYCLING", label: "🚲 Cycling" },
    { value: "WALKING", label: "🚶 Walking" },
  ];

  const purpleStyle = {
    backgroundColor: "#6f42c1",
  };

  return (
    <>
      {load && (
        <>
          <Spinner size="sm" style={{ color: "#d9d9d9" }} /> Loading Map...
        </>
      )}
      <div
        ref={mapRef}
        style={{
          width: "90%",
          height: "250px",
          borderRadius: "10px",
          margin: "0 auto",
        }}
      ></div>

      <Container className="p-3">
        {calcRoute && (
          <div className="text-center my-3">
            <Spinner
              animation="border"
              style={{ color: "#d9d9d9" }}
              size="sm"
            />{" "}
            Calculating Route...
          </div>
        )}

        {error && (
          <div className="alert alert-info">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {error}
          </div>
        )}
        <Row>
            <Col sm={4} lg={4} xs={12}>
            <div className="offcanv-container-itm p-3 rounded mb-3 shadow-sm">
          <Form>
            {transportModes.map((mode) => (
              <Form.Check
                key={mode.value}
                value={""}
                type="radio"
                id={`mode-${mode.value}`}
                label={mode.label}
                checked={routeMode === mode.value}
                onChange={() => setRouteMode(mode.value)}
                className="mb-2 custom-radio"
              />
            ))}
          </Form>
        </div>
            </Col>
            <Col sm={6} lg={6} xs={12}
            style={{
                maxHeight: "400px",
                overflowY: "scroll"
            }}
            >
            {routes.length > 0 && (
          <>
            <div className="offcanv-container-itm p-3 rounded mb-3 shadow-sm">
              <h5 className="mb-3">Available Routes</h5>
              {routes.map((route, index) => (
                <Button
                  key={index}
                  style={routeIndex === index ? purpleStyle : {}}
                  variant={routeIndex === index ? "primary" : "outline-primary"}
                  className="w-100 mb-2 text-start"
                  onClick={() => handleRouteClick(index)}
                >
                  <div
                    style={{
                      color: "white",
                    }}
                  >
                    <div>{route.summary || `Route ${index + 1}`}</div>
                    <small>
                      {route.legs[0].duration.text} •{" "}
                      {route.legs[0].distance.text}
                    </small>
                  </div>
                </Button>
              ))}
            </div>

            {steps.length > 0 && (
              <div className="offcanv-container-itm p-3 rounded mb-3 shadow-sm">
                <h5 className="mb-3">Turn-by-Turn Directions</h5>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="border-start border-primary ps-3 mb-3"
                    style={{ borderColor: "#6f42c1!important" }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: step.instructions }}
                    />
                    <small className="text-muted">
                      {step.distance.text} • {step.duration.text}
                    </small>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
            </Col>
        </Row>

        <Button
          style={purpleStyle}
          className="w-100 mb-3"
          onClick={() =>
            createGoogleMapsNavigationLink(source, dest)
          }
        >
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
          Open in Google Maps
        </Button>
      </Container>
    </>
  );
};

export default EmbeddedChatMap;
